import React, { useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    LinearProgress,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { PublicSessionFileType } from '../../../api/publicSessionApi';
import { UploadEntry } from '../hooks/usePublicShareUploads';
import { TranslateFn } from '../hooks/useSessionFormI18n';

export interface UploadFirstStepProps {
    uploads: UploadEntry[];
    isAnyUploading: boolean;
    onUpload: (file: File, fileType: PublicSessionFileType) => Promise<UploadEntry>;
    onContinueWithoutUploads: () => void;
    onParseWithAi: () => Promise<void> | void;
    parsing: boolean;
    t: TranslateFn;
    /**
     * When true the widget renders in "documents" mode: tighter padding, a
     * different (less prominent) title, and the two big "Skip" / "Use these
     * to pre-fill" CTAs are hidden. Used after the worker has already run AI
     * prefill OR explicitly dismissed the hero — the file picker stays
     * available so they can still upload VCA / ID / CERT documents (the
     * worker's actual recruitment file set, not just the CV).
     */
    compact?: boolean;
}

const FILE_TYPE_KEY: Record<PublicSessionFileType, string> = {
    CV: 'file_type_cv',
    VCA: 'file_type_vca',
    RIJBEWIJS: 'file_type_driver_license',
    REFERENCE: 'file_type_reference',
    ID_DOCUMENT: 'file_type_id_document',
    CERTIFICATION: 'file_type_certification',
    OTHER: 'file_type_other',
};

export const UploadFirstStep: React.FC<UploadFirstStepProps> = ({
    uploads,
    isAnyUploading,
    onUpload,
    onContinueWithoutUploads,
    onParseWithAi,
    parsing,
    t,
    compact = false,
}) => {
    const [pendingType, setPendingType] = useState<PublicSessionFileType>('CV');
    const inputRef = useRef<HTMLInputElement | null>(null);

    const handlePickClick = () => inputRef.current?.click();

    const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        await onUpload(file, pendingType);
    };

    const hasSuccessfulUploads = uploads.some((u) => u.status === 'done');

    return (
        <Paper
            elevation={compact ? 1 : 2}
            sx={{
                p: compact ? { xs: 2, md: 2.5 } : { xs: 3, md: 4 },
                mb: compact ? 3 : 4,
                borderRadius: 3,
            }}
        >
            <Stack spacing={2}>
                <Typography variant={compact ? 'subtitle1' : 'h5'} sx={{ fontWeight: 600 }}>
                    {t(compact ? 'upload_more_title' : 'upload_title')}
                </Typography>
                {!compact && (
                    <Typography variant="body2" color="text.secondary">
                        {t('upload_subtitle')}
                    </Typography>
                )}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="stretch">
                    <TextField
                        select
                        label={t('upload_label_type')}
                        value={pendingType}
                        onChange={(e) => setPendingType(e.target.value as PublicSessionFileType)}
                        size="small"
                        sx={{ minWidth: 220 }}
                    >
                        {(Object.entries(FILE_TYPE_KEY) as Array<[PublicSessionFileType, string]>).map(
                            ([key, translationKey]) => (
                                <MenuItem key={key} value={key}>
                                    {t(translationKey)}
                                </MenuItem>
                            ),
                        )}
                    </TextField>
                    <Button variant="contained" onClick={handlePickClick} disabled={isAnyUploading}>
                        {isAnyUploading ? <CircularProgress size={20} /> : t('upload_btn_choose')}
                    </Button>
                    <input
                        ref={inputRef}
                        type="file"
                        hidden
                        onChange={handleInputChange}
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,.gif,.txt,.md,.csv,.html,.json"
                    />
                </Stack>

                {uploads.length > 0 && (
                    <Stack spacing={1} sx={{ mt: 2 }}>
                        {uploads.map((u) => (
                            <Box
                                key={u.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    bgcolor: 'background.default',
                                    borderRadius: 2,
                                    p: 1.5,
                                }}
                            >
                                {u.status === 'done' && (
                                    <CheckCircleOutlineIcon color="success" fontSize="small" />
                                )}
                                {u.status === 'error' && (
                                    <ErrorOutlineIcon color="error" fontSize="small" />
                                )}
                                {u.status === 'uploading' && <CircularProgress size={16} />}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" noWrap>
                                        {u.file.name}
                                    </Typography>
                                    {u.status === 'uploading' && (
                                        <LinearProgress
                                            variant="determinate"
                                            value={u.progress}
                                            sx={{ mt: 0.5 }}
                                        />
                                    )}
                                    {u.status === 'error' && (
                                        <Typography variant="caption" color="error.main">
                                            {u.error}
                                        </Typography>
                                    )}
                                </Box>
                                <Chip
                                    size="small"
                                    label={t(FILE_TYPE_KEY[u.fileType])}
                                    variant="outlined"
                                />
                            </Box>
                        ))}
                    </Stack>
                )}

                {parsing && (
                    <Alert severity="info" icon={<CircularProgress size={18} />}>
                        {t('upload_parsing_alert')}
                    </Alert>
                )}

                {!compact && (
                    <Stack
                        direction="row"
                        spacing={2}
                        justifyContent="flex-end"
                        sx={{ pt: 1 }}
                        flexWrap="wrap"
                    >
                        <Button
                            variant="text"
                            color="inherit"
                            onClick={onContinueWithoutUploads}
                            disabled={isAnyUploading || parsing}
                        >
                            {t('upload_btn_skip')}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => void onParseWithAi()}
                            disabled={!hasSuccessfulUploads || isAnyUploading || parsing}
                        >
                            {t('upload_btn_prefill')}
                        </Button>
                    </Stack>
                )}
            </Stack>
        </Paper>
    );
};
